<template>
    <header class="CaseStudy__hero" data-speed="clamp(0.5)">
        <VimeoVideo
            v-if="caseStudy.data.hero_video_vimeo_id"
            :is-background-video="true"
            :video-id="caseStudy.data.hero_video_vimeo_id"
            :player-id="
                'media-background-hero--' + caseStudy.data.hero_video_vimeo_id
            "
            :show-play-icon="false"
            :show-white-frame="false"
            @loaded="imageLoaded = true"
            class="CaseStudy__hero-image media-bg-hero__media--video"
        />
        <SmartImage
            v-else
            class="CaseStudy__hero-image"
            :url="caseStudy.data.hero_image.url"
            :srcs="[
                { width: 400 },
                { width: 800 },
                { width: 1000 },
                { width: 1300 },
            ]"
            :padding="false"
            :with-placeholder="false"
            :width="900"
            :height="1440"
            :as-bg="true"
            :lazy-load="false"
        />
        <div class="CaseStudy__hero-content">
            <h1 class="CaseStudy__hero-title">
                {{ caseStudy.data.title || caseStudy.data.card_title }}
            </h1>
            <ul class="CaseStudy__hero-tags" v-if="capabilityTags.length">
                <li
                    v-for="capabilityItem in capabilityTags"
                    :key="capabilityItem.capability.id"
                >
                    <CustomTag
                        :text="capabilityItem.capability.data.capability_name"
                        :filled="true"
                    />
                </li>
            </ul>
        </div>
    </header>
    <main class="CaseStudy" ref="mainSection">
        <div class="CaseStudy__stats" v-if="hasStats">
            <ul class="CaseStudy__stats-list">
                <template v-for="(stat, statIndex) in caseStudy?.data.stats">
                    <li
                        v-if="stat.stat_title || stat.stat_figure"
                        :key="statIndex"
                        class="CaseStudy__stats-item"
                    >
                        <div class="CaseStudy__stats-item-title">
                            {{ stat.stat_title }}
                        </div>
                        <div class="CaseStudy__stats-item-desc">
                            {{ stat.stat_figure }}
                        </div>
                    </li>
                </template>
            </ul>
        </div>
        <SliceZone
            v-if="caseStudy?.data.slices"
            wrapper="div"
            :slices="caseStudy?.data.slices ?? []"
            :components="components"
            class="CaseStudy__slice-zone"
        />
        <CustomLink
            :to="caseStudy?.data.next_case_study.url"
            class="CaseStudy__next-article-link"
            cursor-type="view"
            cursor-text="Next <br/> Project"
        >
            <section
                v-if="caseStudy?.data.next_case_study.uid"
                class="CaseStudy__next-article"
            >
                <SmartPicture
                    class="CaseStudy__next-article-image"
                    :small-image="
                        caseStudy?.data.next_case_study.data?.hero_image
                            ?.portrait?.url ||
                        caseStudy?.data.next_case_study.data?.hero_image?.small
                            ?.url
                    "
                    :medium-image="
                        caseStudy?.data.next_case_study.data?.hero_image?.medium
                            ?.url
                    "
                    :large-image="
                        caseStudy?.data.next_case_study.data?.hero_image?.url
                    "
                    :width="
                        caseStudy?.data.next_case_study.data?.hero_image
                            ?.dimensions.width
                    "
                    :height="
                        caseStudy?.data.next_case_study.data?.hero_image
                            ?.dimensions?.height
                    "
                    :is-background="true"
                />
                <div class="CaseStudy__next-article-content">
                    <div class="CaseStudy__next-article-content-wrapper">
                        <p class="CaseStudy__next-article-prefix">
                            Next Project
                        </p>
                        <h4 class="CaseStudy__next-article-title">
                            {{ caseStudy?.data.next_case_study.data?.title }}
                        </h4>
                        <ul
                            class="CaseStudy__next-article-list"
                            v-if="nextCaseStudyCapabilityTags.length"
                        >
                            <li
                                v-for="capabilityItem in nextCaseStudyCapabilityTags"
                                :key="capabilityItem.capability.id"
                            >
                                <CustomTag
                                    :text="
                                        capabilityItem.capability.data
                                            .capability_name
                                    "
                                    :filled="true"
                                />
                            </li>
                        </ul>
                    </div>
                </div>
            </section>
        </CustomLink>
    </main>
</template>

<script setup>
import { components } from '~/slices'

const prismic = usePrismic()
const route = useRoute()
const mainSection = ref()
const mainSectionObserver = ref(null)
const coreStore = useCoreStore()

const { data: caseStudy } = await useAsyncData(
    `[case-study-${route.params.work[0]}-lala]`,
    () =>
        prismic.client.getByUID('case_study', route.params.work, {
            fetchLinks: [
                'brand.client_name',
                'brand.client_logo',
                'capability.capability_name',
                'case_study.hero_image',
                'case_study.title',
                'case_study.capability_tags',
            ],
        }),
)

const hasStats = computed(() => {
    return (
        caseStudy.value.data.stats.filter(
            (stat) => stat.stat_title || stat.stat_figure,
        ).length > 0
    )
})

const capabilityTags = computed(() => {
    return caseStudy.value.data.capability_tags?.filter((item) => item.data)
})

const nextCaseStudyCapabilityTags = computed(() => {
    return (
        caseStudy?.data?.next_case_study.data?.capability_tags?.filter(
            (item) => item.data,
        ) || []
    )
})

useSeoMeta({
    title:
        caseStudy.value?.data?.meta_title ||
        caseStudy.value?.data?.title ||
        'eight&four | A Performance Driven Social and Content Agency',
    description:
        caseStudy.value?.data?.meta_description ||
        caseStudy.value?.data?.card_title ||
        "We're a social & end-to-end content production agency. We work with global brands looking for a dynamic, multi-lingual agency.",
    ogTitle:
        caseStudy.value?.data?.meta_title ||
        caseStudy.value?.data?.title ||
        'eight&four | A Performance Driven Social and Content Agency',
    ogDescription:
        caseStudy.value?.data?.meta_description ||
        caseStudy.value?.data?.card_title ||
        "We're a social & end-to-end content production agency. We work with global brands looking for a dynamic, multi-lingual agency.",
    ogImage:
        caseStudy.value?.data?.social_image?.url ||
        caseStudy.value?.data?.hero_image?.social?.url ||
        process.env.BASE_URL + '/images/default-og-image.png',
})

useHead({
    bodyAttrs: {
        class: 'theme--white hide-footer work-page',
    },
})

onMounted(() => {
    const { smoothScroller } = useGsap()

    smoothScroller?.effects('[data-speed]')

    mainSectionObserver.value = new IntersectionObserver(
        (entries, _observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    coreStore.setLogoColor('black')
                } else {
                    coreStore.setLogoColor('white')
                }
            })
        },
        {
            rootMargin: '0px 0px -100% 0px',
        },
    )

    mainSectionObserver.value.observe(mainSection.value)
})

onBeforeUnmount(() => {
    mainSectionObserver.value.disconnect()
})
</script>

<style lang="scss" scoped>
.CaseStudy {
    @include grid;
    background-color: var(--white);
    position: relative;
}

.CaseStudy__hero {
    @include grid;

    grid-column: 1 / span 24;
    width: 100%;
    height: 100vh;
    height: 100dvh;
    position: relative;
}

.CaseStudy__hero-image {
    position: absolute;
    width: 100%;
    height: 100%;

    &::after {
        content: '';
        display: block;
        position: absolute;
        z-index: 1;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: radial-gradient(
            44.39% 44.39% at 50% 71.61%,
            rgba(0, 0, 0, 0.7) 0%,
            rgba(0, 0, 0, 0) 100%
        );
    }
}

.CaseStudy__hero-content {
    grid-column: 2 / span 22;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
    z-index: 1;
    margin-top: auto;
    max-width: 70.3125rem;
    text-align: center;
    padding-bottom: 9rem;
    margin-left: auto;
    margin-right: auto;
}

.CaseStudy__hero-title {
    @include heading-2;

    margin-bottom: 1.25rem;
}

.CaseStudy__hero-tags {
    display: flex;
    gap: 1.25rem;
}

.CaseStudy__stats {
    grid-column: 1 / span 24;
    color: var(--white);

    @include medium-up {
        grid-column: 2 / span 22;
        transform: translateY(-35%);
        position: relative;
        z-index: 1;
    }

    @include large-up {
        grid-column: 4 / span 18;
    }
}

.CaseStudy__stats-list {
    display: flex;
    flex-wrap: wrap;
    column-gap: clamp(3rem, 6vw, 7rem);
    row-gap: 2rem;
    margin-left: auto;
    margin-right: auto;
    padding: 3rem clamp(1rem, 6vw, 4rem);
    background-color: var(--black-30);

    @include large-up {
        max-width: clamp(50rem, 70vw, 65rem);
        text-align: center;
        justify-content: center;
    }
}

.CaseStudy__stats-item {
    flex-grow: 1;

    @include medium-up {
        max-width: 25%;
    }
}

.CaseStudy__stats-item-title {
    @include label-lg;

    margin-bottom: 0.75rem;
}

.CaseStudy__stats-item-desc {
    @include body-lg;

    font-weight: var(--font-weight-bold);
}

.CaseStudy__slice-zone {
    @include grid;

    grid-column: 1 / span 24;
}

.CaseStudy__next-article-link {
    grid-column: 1 / span 24;
    margin-top: 8rem;

    &:hover {
        .CaseStudy__next-article-image {
            transform: scale(1.01);
        }
    }

    @include large-up {
        margin-top: 11.25rem;
    }
}

.CaseStudy__next-article {
    position: relative;
    min-height: 100dvh;
    overflow: hidden;

    @include medium-only {
        min-height: 50dvh;
    }

    &::after {
        content: '';
        display: block;
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        bottom: 0;
        z-index: 1;
        background: radial-gradient(
            79.36% 39.98% at 50% 71.61%,
            rgba(0, 0, 0, 0.7) 0%,
            rgba(0, 0, 0, 0) 100%
        );

        @include large-up {
            background: radial-gradient(
                44.39% 44.39% at 50% 71.61%,
                rgba(0, 0, 0, 0.7) 0%,
                rgba(0, 0, 0, 0) 100%
            );
        }
    }
}

.CaseStudy__next-article-image {
    position: absolute;
    height: 100%;
    width: 100%;
    transition: transform 0.4s ease;
}

.CaseStudy__next-article-content {
    @include grid;

    grid-column: 2 / span 22;
    position: absolute;
    bottom: 0;
    z-index: 2;
    width: 100%;
    text-align: center;
    margin-bottom: clamp(4rem, 10%, 6rem);
}

.CaseStudy__next-article-content-wrapper {
    grid-column: 2 / span 22;
}

.CaseStudy__next-article-list {
    display: flex;
    justify-content: center;
    gap: 1.25rem;
}

.CaseStudy__next-article-prefix {
    @include label-lg;

    margin-bottom: 1.25rem;
}

.CaseStudy__next-article-title {
    @include heading-2;

    margin-bottom: 1.25rem;
}
</style>
